// Plans/2026-10-19-vorab-bestand-plan.md (Schnitt 2): Vorab-Bestand aus dem Wareneingang.
// Nur Plattform: kein DR, keine Portale, `quantity` bleibt der DR-Spiegel.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from './client'

export interface VorabDialogInfo {
  receipt_item_id: number
  merchant_sku: string
  stock_item_id: number | null
  bestand_dr: number | null
  /** Schon offene Vorab-Stueck des Kopfes (alle Positionen/Container). */
  vorab_offen_kopf: number
  stueck_je_karton: number | null
  max_kartons: number
  zaehlbasis: boolean
  moeglich: boolean
  sperre: string | null
  vorab: null | { id: number; stueck: number; status: 'offen' | 'aufgegangen' | 'storniert'; created_at: string; created_by_name: string | null }
}

export interface VorabOffenZeile {
  id: number
  stock_item_id: number
  sku: string
  merchant_sku: string | null
  name: string | null
  quantity: number
  stueck: number
  /** Summe aller offenen Vorab-Stueck des Kopfes; Bestand = quantity + vorab_offen_kopf. */
  vorab_offen_kopf: number
  bestand_vor: number
  receipt_id: number
  receipt_item_id: number
  shipment_id: number
  order_number: string | null
  created_at: string
  created_by_name: string | null
  evtl_doppelt: boolean
  dr_plus: number | null
}

function invalidiere(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['warehouse'] })
  void qc.invalidateQueries({ queryKey: ['purchase'] })
  void qc.invalidateQueries({ queryKey: ['vorab'] })
  void qc.invalidateQueries({ queryKey: ['grundartikel'] })
  void qc.invalidateQueries({ queryKey: ['inventory-stock'] })
  void qc.invalidateQueries({ queryKey: ['catalog-grundartikel'] })
}

export function useVorabDialogInfo(receiptId: number, receiptItemId: number, enabled: boolean) {
  return useQuery({
    queryKey: ['vorab', 'item', receiptId, receiptItemId],
    queryFn: () => apiFetch<VorabDialogInfo>(`/warehouse/receipt/${receiptId}/items/${receiptItemId}/vorab/`),
    enabled,
  })
}

export function useVorabEinbuchen(receiptId: number, receiptItemId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { cartons: number }) =>
      apiFetch<{ vorab_id: number; stock_item_id: number; cartons: number; stueck: number; duplicate: boolean }>(
        `/warehouse/receipt/${receiptId}/items/${receiptItemId}/vorab/`, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => invalidiere(qc),
  })
}

export function useVorabKorrigieren(receiptId: number, receiptItemId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { stueck: number; grund: string }) =>
      apiFetch<{ vorab_id: number; stueck_vorher: number; stueck: number; changed: boolean }>(
        `/warehouse/receipt/${receiptId}/items/${receiptItemId}/vorab-korrektur/`, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => invalidiere(qc),
  })
}

export function useVorabOffen(enabled = true) {
  return useQuery({
    queryKey: ['vorab', 'offen'],
    queryFn: async () => (await apiFetch<{ items: VorabOffenZeile[] }>('/warehouse/vorab/offen/')).items,
    enabled,
  })
}

export function useVorabUebernehmen() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, grund }: { id: number; grund: string }) =>
      apiFetch<{ ok: true }>(`/warehouse/vorab/${id}/uebernehmen/`, { method: 'POST', body: JSON.stringify({ grund }) }),
    onSuccess: () => invalidiere(qc),
  })
}
